import type {
    ChangePasswordInput,
    ClaimLinkResponse,
    FixInviteInput,
    InviteMemberInput,
    InviteMemberResponse,
    OrgMember,
    OrgMemberListItem,
    PatchMemberInput,
    SuccessResponse
} from "../types/orgMembers";

import { assertApiSuccess, readApiJson } from "./apiResponse";
import { orgAuthFetch } from "./orgAuthFetch";
import type { OAuthConfig } from "./orgAuthService";

interface OrgMembersListResponse {
    data: OrgMemberListItem[];
    total: number | null;
    limit: number | null;
    offset: number | null;
    page: number | null;
}

export async function fetchOrgMembers(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">
): Promise<OrgMembersListResponse> {
    const response = await orgAuthFetch(`${config.apiBase}/api/org/console/members?limit=200`, undefined, config);

    return readApiJson<OrgMembersListResponse>(response, "Failed to fetch organization members");
}

export async function inviteMember(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    input: InviteMemberInput
): Promise<InviteMemberResponse> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/members`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input)
        },
        config
    );

    return readApiJson<InviteMemberResponse>(response, "Failed to invite organization member");
}

export async function resetMemberPassword(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    uuid: string
): Promise<ClaimLinkResponse> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/members/${uuid}/reset-password`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({})
        },
        config
    );

    return readApiJson<ClaimLinkResponse>(response, "Failed to reset member password");
}

export async function regenerateMemberClaim(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    uuid: string
): Promise<ClaimLinkResponse> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/members/${uuid}/regenerate-claim`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({})
        },
        config
    );

    return readApiJson<ClaimLinkResponse>(response, "Failed to regenerate member claim link");
}

export async function fixMemberInvite(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    uuid: string,
    input: FixInviteInput
): Promise<InviteMemberResponse> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/members/${uuid}/fix-invite`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input)
        },
        config
    );

    return readApiJson<InviteMemberResponse>(response, "Failed to update member invitation");
}

export async function cancelMemberInvitation(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    uuid: string
): Promise<void> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/members/${uuid}/cancel-invite`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({})
        },
        config
    );

    await assertApiSuccess(response, "Failed to cancel member invitation");
}

export async function patchMember(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    uuid: string,
    input: PatchMemberInput
): Promise<OrgMember> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/members/${uuid}`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input)
        },
        config
    );

    return readApiJson<OrgMember>(response, "Failed to update organization member");
}

export async function changeOwnPassword(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    input: ChangePasswordInput
): Promise<SuccessResponse> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/me/password`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input)
        },
        config
    );

    return readApiJson<SuccessResponse>(response, "Failed to change password");
}

export async function signOutAllSessions(
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">
): Promise<SuccessResponse> {
    const response = await orgAuthFetch(
        `${config.apiBase}/api/org/console/me/sign-out-all`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({})
        },
        config
    );

    return readApiJson<SuccessResponse>(response, "Failed to sign out sessions");
}
